import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

import DashboardLayout from "./DashboardLayout";
import OverviewPage from "./OverviewPage";
import MessagingPage from "./MessagingPage";
import SecurityPage from "./SecurityPage";
import DeveloperDocsPage from "./DeveloperDocsPage";
import ContactsPage from "./ContactsPage";

import { StandardURL } from "../App";

const VidEnvironsnew = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const contactsFileInputRef = useRef(null); 

  // --- UI & NAVIGATION STATE ---
  const [currentTab, setCurrentTab] = useState("overview"); 
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  
  // --- CORE DATA STATE ---
  const [vid, setVid] = useState("");
  const [credentials, setCredentials] = useState(null);
  const [isLoadingCreds, setIsLoadingCreds] = useState(true);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [newHashkey, setNewHashkey] = useState(null);
  const [hashTimer, setHashTimer] = useState(0);

  // --- MESSAGING & CAMPAIGNS STATE ---
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStats, setUploadStats] = useState({ total: 0, duplicates: 0, contacts: [] });
  const [manualPhone, setManualPhone] = useState("");
  const [messageData, setMessageData] = useState({ message: "", sendType: "now", scheduledTime: "" });
  const [isSending, setIsSending] = useState(false);

  // --- NEW CONTACTS APP STATE ---
  const [contactsList, setContactsList] = useState([]);
  const [isContactsLoading, setIsContactsLoading] = useState(false);
  
  const [stagedContacts, setStagedContacts] = useState([]); 
  const [categoryName, setCategoryName] = useState("");
  const [manualContact, setManualContact] = useState({ name: "", phone_number: "" });
  
  const [batchMessageData, setBatchMessageData] = useState({ message: "", sendType: "now", scheduledTime: "" });
  const [isSendingBatch, setIsSendingBatch] = useState(false);

  // --- SECURITY & COMPLIANCE STATE ---
  const [topupForm, setTopupForm] = useState({ amount: "", phone_number: "" });
  const [isTopupLoading, setIsTopupLoading] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ oldPassword: "", newPassword: "", confirmPassword: "" });
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  
  // --- KYC STATE ---
  const [kycViewData, setKycViewData] = useState(null);
  const [isKycLoading, setIsKycLoading] = useState(false);
  const [uploadedKycDocs, setUploadedKycDocs] = useState({ identification: "", krapin: "" });
  const [kycFileNames, setKycFileNames] = useState({ identification: "", krapin: "" });

  // --- HISTORY & SCHEDULED STATE ---
  const [historyYear, setHistoryYear] = useState("");
  const [historyData, setHistoryData] = useState([]);
  const [scheduledData, setScheduledData] = useState([]); 
  const [filterParams, setFilterParams] = useState({ phone: "", start_date: "", end_date: "", message: "" });

  // --- INITIALIZATION ---
  useEffect(() => {
    const currentVid = localStorage.getItem("currentVid");
    if (!currentVid) {
      toast.error("Session configuration expired.");
      navigate("/caretaker/landing");
      return;
    }
    setVid(currentVid);
    fetchCredentials(currentVid);
    checkKycStatus(currentVid);
    fetchLogData(currentVid);
    fetchScheduledData(currentVid); 
    fetchContactsDirectory(currentVid);
  }, [navigate]);

  useEffect(() => {
    let interval;
    if (hashTimer > 0) interval = setInterval(() => setHashTimer((prev) => prev - 1), 1000);
    else if (hashTimer === 0 && newHashkey) setNewHashkey(null);
    return () => clearInterval(interval);
  }, [hashTimer, newHashkey]);

  // --- CORE API HANDLERS ---
  const fetchCredentials = async (currentVid) => {
    setIsLoadingCreds(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${StandardURL}/sms/credentials/uservid/${currentVid}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      const data = res.data.response ? res.data.response[0] : res.data;
      setCredentials(data);
      if (data.webhook) setWebhookUrl(data.webhook);
    } catch (error) { toast.error("Error connecting credentials."); }
    finally { setIsLoadingCreds(false); }
  };

  const checkKycStatus = async (currentVid) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${StandardURL}/sms/kyc/check/${currentVid}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });

      if (res.data.success && !res.data.needsKyc) {
        const viewRes = await axios.get(`${StandardURL}/sms/kyc/view/${currentVid}`, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        setKycViewData(viewRes.data.response || viewRes.data);
      }
    } catch (err) { console.error("KYC Check failed", err); }
  };

  const fetchLogData = async (currentVid) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${StandardURL}/sms/messages/history/${currentVid}/this-year`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setHistoryData(res.data.response || []);
    } catch (e) { console.error("Logs fetch failed", e); }
  };

  const fetchScheduledData = async (currentVid) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${StandardURL}/sms/scheduled/view/${currentVid}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setScheduledData(res.data.response || res.data || []);
    } catch (e) { console.error("Scheduled fetch failed", e); }
  };

  const filterLogsByYear = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      let res;
      if (historyYear) {
        res = await axios.post(`${StandardURL}/sms/messages/history/${vid}/by-year`, { year: historyYear }, config);
      } else {
        res = await axios.get(`${StandardURL}/sms/messages/history/${vid}/this-year`, config);
      }
      setHistoryData(res.data.response || []);
    } catch (e) { toast.error("Failed executing history filter."); }
  };

  const handleFilterMessages = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${StandardURL}/sms/messages/history/${vid}/filter`, filterParams, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistoryData(res.data.response || []);
      toast.success("Filters applied successfully.");
    } catch (e) {
      toast.error(e.response?.data?.response || "Failed to apply filters.");
    }
  };

  const handleWebhookUpdate = async () => {
    if (!webhookUrl) return toast.warning("Webhook URL required.");
    try {
      const token = localStorage.getItem("token");
      await axios.patch(`${StandardURL}/sms/credentials/webhook/${vid}`, { webhook: webhookUrl }, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      toast.success("Webhook updated.");
      fetchCredentials(vid);
    } catch (e) { toast.error("Error updating webhook."); }
  };

  const handleGenerateHashkey = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.patch(`${StandardURL}/sms/credentials/hashkey/${vid}`, {}, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      if (res.data?.response?.hashkey) {
        setNewHashkey(res.data.response.hashkey);
        setHashTimer(15);
        toast.success("New API key generated.");
      }
    } catch (e) { toast.error("Error generating API key."); }
  };

  // --- CONTACTS CONTROLLER API WRAPPERS ---
  const fetchContactsDirectory = async (targetVid) => {
    setIsContactsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${StandardURL}/sms/contacts/vendor/${targetVid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContactsList(res.data.response || []);
    } catch (e) {
      console.error("Failed fetching contacts directory", e);
      setContactsList([]);
    } finally {
      setIsContactsLoading(false);
    }
  };

  const handleStageManualContact = () => {
    if (!manualContact.phone_number || !manualContact.name) {
      return toast.warning("Name and Phone number fields are required.");
    }
    if (!categoryName) {
      return toast.warning("Please specify a Target Category before adding contacts.");
    }
    setStagedContacts((prev) => [
      ...prev,
      { name: manualContact.name, phone_number: manualContact.phone_number, category: categoryName }
    ]);
    setManualContact({ name: "", phone_number: "" });
  };

  const handleImportContactsFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!categoryName) {
      if (contactsFileInputRef.current) contactsFileInputRef.current.value = "";
      return toast.warning("Please specify a Target Category before uploading a manifest.");
    }

    const formData = new FormData();
    formData.append("file", file);
    setIsUploading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${StandardURL}/sms/contacts/import`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      
      const responseBody = res.data.response;
      if (responseBody && responseBody.contacts) {
        const mappedContacts = responseBody.contacts.map(c => ({
          name: c.name,
          phone_number: c.phone_number,
          category: categoryName
        }));

        setStagedContacts((prev) => [...prev, ...mappedContacts]);
        toast.info(`Spreadsheet parsed. ${mappedContacts.length} numbers staged for review.`);
      }
    } catch (err) {
      toast.error("Error parsing manifest elements.");
    } finally {
      setIsUploading(false);
      if (contactsFileInputRef.current) contactsFileInputRef.current.value = "";
    }
  };

  const handleRemoveStagedContact = (index) => {
    setStagedContacts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveStagedContacts = async () => {
    if (stagedContacts.length === 0) return toast.warning("No contacts staged to save.");
    
    setIsContactsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        vid: vid,
        category: categoryName || "General",
        contacts: stagedContacts.map(c => ({
          name: c.name,
          phone_number: c.phone_number
        }))
      };

      await axios.post(`${StandardURL}/sms/contacts/add/${vid}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`${stagedContacts.length} contacts saved into database successfully.`);
      setStagedContacts([]); 
      setCategoryName(""); 
      fetchContactsDirectory(vid); 
    } catch (e) {
      toast.error(`Encountered an error saving the batch.`);
      fetchContactsDirectory(vid);
    } finally {
      setIsContactsLoading(false);
    }
  };

  const handleUpdateContactJson = async (rowId, formValues) => {
    try {
      const token = localStorage.getItem("token");
      await axios.patch(`${StandardURL}/sms/contacts/update-json/${vid}/${rowId}`, formValues, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Internal contact data updated.");
      fetchContactsDirectory(vid);
    } catch (e) { toast.error("Failed updating internal contact values."); }
  };

  const handleUpdateCategory = async (rowId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.patch(`${StandardURL}/sms/contacts/update-category/${vid}/${rowId}`, {
        category: categoryName || "General"
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Contact category updated.");
      fetchContactsDirectory(vid);
    } catch (e) { toast.error("Failed restructuring database category column."); }
  };

  const handleClearJson = async (rowId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${StandardURL}/sms/contacts/clear-json/${vid}/${rowId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("JSON content cleared successfully.");
      fetchContactsDirectory(vid);
    } catch (e) { toast.error("Failed clearing JSON properties."); }
  };

  const handleDeleteRow = async (rowId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${StandardURL}/sms/contacts/remove/${rowId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Database table row permanently deleted.");
      fetchContactsDirectory(vid);
    } catch (e) { toast.error("Failed tracking deletion query commands."); }
  };

  // -------------------------------------------------------------
  // SCHEDULE / BATCH DISPATCH LOGIC FOR THE CONTACTS DIRECTORY
  // -------------------------------------------------------------
  const handleSendBatchByCategory = async (selectedCategory) => {
    if (!batchMessageData.message) return toast.warning("Message payload cannot be empty.");
    
    const filteredRows = contactsList.filter(c => selectedCategory === "ALL" || c.category === selectedCategory);
    
    // HEAVY SANITIZATION: Ensure array only contains strictly formatted strings 
    // to match phonesArray logic perfectly and prevent 400 Bad Request
    const targetPhones = [];
    filteredRows.forEach(row => {
      try {
        const parsed = typeof row.description === "string" ? JSON.parse(row.description) : row.description;
        if (parsed && parsed.phone_number) {
          targetPhones.push(String(parsed.phone_number).trim());
        }
      } catch (e) { }
    });

    if (targetPhones.length === 0) return toast.error("No valid phone entries mapped to this category.");

    setIsSendingBatch(true);
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      console.log("Dispatching Batch Payload:", { phones: targetPhones, message: batchMessageData.message });

      if (batchMessageData.sendType === "scheduled") {
        if (!batchMessageData.scheduledTime) throw new Error("Scheduling time is required.");
        
        await axios.post(`${StandardURL}/sms/send/schedule/${vid}`, { 
          phones: targetPhones, 
          message: batchMessageData.message, 
          scheduled_time: batchMessageData.scheduledTime 
        }, config);
        
        toast.success("Category batch messages scheduled.");
        fetchScheduledData(vid); 
      } else {
        if (targetPhones.length === 1) {
           await axios.post(`${StandardURL}/sms/send/single/${vid}`, { phones: targetPhones[0], message: batchMessageData.message }, config);
        } else {
           await axios.post(`${StandardURL}/sms/send/batch/${vid}`, { phones: targetPhones, message: batchMessageData.message }, config);
        }
        toast.success(`Campaign successfully broadcast to ${targetPhones.length} recipients.`);
      }

      setBatchMessageData({ ...batchMessageData, message: "" });
      fetchCredentials(vid);
      fetchLogData(vid);
    } catch (e) { 
      console.error("Batch dispatch error:", e);
      toast.error(e.response?.data?.response || e.message || "Failed executing campaign broadcast command."); 
    } 
    finally { setIsSendingBatch(false); }
  };

  // --- GENERAL CAMPAIGN FILE UPLOAD (MESSAGING PAGE) ---
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setIsUploading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${StandardURL}/sms/contacts/import`, formData, { 
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } 
      });
      const rData = res.data.response;
      if (rData) {
        setUploadStats(prev => ({
          total: prev.total + (rData.total || 0),
          duplicates: prev.duplicates + (rData.duplicates?.count || 0),
          contacts: [...prev.contacts, ...(rData.contacts || [])]
        }));
        toast.success("Contacts imported successfully.");
      }
    } catch (e) { toast.error("Error parsing contact file."); }
    finally { setIsUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const addManualContact = () => {
    if (!manualPhone) return;
    setUploadStats(prev => ({
      ...prev,
      total: prev.total + 1,
      contacts: [{ name: "Manual Entry", phone_number: manualPhone }, ...prev.contacts]
    }));
    setManualPhone("");
  };

  const removeContact = (index) => {
    setUploadStats(prev => {
      const newContacts = prev.contacts.filter((_, i) => i !== index);
      return { ...prev, total: newContacts.length, contacts: newContacts };
    });
  };

  const handleSendMessage = async () => {
    if (uploadStats.contacts.length === 0) return toast.warning("No recipients added.");
    if (!messageData.message) return toast.warning("Message content cannot be empty.");
    
    setIsSending(true);
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const phonesArray = uploadStats.contacts.map(c => c.phone_number);

      if (messageData.sendType === "scheduled") {
        if (!messageData.scheduledTime) throw new Error("Scheduling time is required.");
        await axios.post(`${StandardURL}/sms/send/schedule/${vid}`, { 
          phones: phonesArray, message: messageData.message, scheduled_time: messageData.scheduledTime 
        }, config);
        toast.success("Messages scheduled.");
        fetchScheduledData(vid); 
      } else {
        if (phonesArray.length === 1) {
          await axios.post(`${StandardURL}/sms/send/single/${vid}`, { phones: phonesArray[0], message: messageData.message }, config);
        } else {
          await axios.post(`${StandardURL}/sms/send/batch/${vid}`, { phones: phonesArray, message: messageData.message }, config);
        }
        toast.success("Messages dispatched.");
      }
      setMessageData({ ...messageData, message: "" });
      fetchCredentials(vid);
      fetchLogData(vid);
    } catch (e) { toast.error(e.response?.data?.response || e.message || "Failed to send messages."); }
    finally { setIsSending(false); }
  };

  const handleRetryMessage = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${StandardURL}/sms/send/sendretry/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Message retried successfully.");
      fetchLogData(vid); 
      fetchCredentials(vid); 
    } catch (e) { toast.error("Failed to retry message."); }
  };

  const handleDeleteScheduled = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${StandardURL}/sms/scheduled/delete/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Scheduled message deleted.");
      fetchScheduledData(vid); 
    } catch (e) { toast.error("Failed to delete scheduled message."); }
  };

  const handleUpdateScheduledTime = async (id, newTime) => {
    if (!newTime) return toast.warning("Please provide a valid time.");
    try {
      const token = localStorage.getItem("token");
      await axios.patch(`${StandardURL}/sms/scheduled/update/${id}`, { scheduled_time: newTime }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Scheduled time updated.");
      fetchScheduledData(vid); 
    } catch (e) { toast.error("Failed to update scheduled time."); }
  };

  const handleTopUpSubmit = async (e) => {
    e.preventDefault(); 
    if (Number(topupForm.amount) < 1000) return toast.error("Minimum top-up is KES 1000.");
    setIsTopupLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${StandardURL}/sms/payment/topup-mpesa/${vid}`, topupForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("STK Push dispatched to phone.");
      setTopupForm({ amount: "", phone_number: "" });
      fetchCredentials(vid);
    } catch (e) { toast.error("Payment initialization failed."); }
    finally { setIsTopupLoading(false); }
  };

  const handleKycFileUpload = async (fieldName, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      setIsKycLoading(true);
      const token = localStorage.getItem("token");
      const res = await axios.post(`${StandardURL}/sms/kyc/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
      });
      const fileReference = res.data?.coverUri || res.data?.response?.coverUri || "";
      setUploadedKycDocs((prev) => ({ ...prev, [fieldName]: fileReference }));
      setKycFileNames((prev) => ({ ...prev, [fieldName]: file.name }));
      toast.success(`${fieldName.replace("_", " ")} uploaded successfully`);
    } catch (error) { toast.error(`Failed to upload ${fieldName.replace("_", " ")}`); } 
    finally { setIsKycLoading(false); }
  };

  const handleKycSubmit = async (e) => {
    e.preventDefault();
    const missing = Object.entries(uploadedKycDocs).filter(([_, value]) => !value).map(([key]) => key.replace("_", " "));
    if (missing.length > 0) return toast.error(`Please upload: ${missing.join(", ")}`);

    setIsKycLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${StandardURL}/sms/kyc/add/${vid}`, uploadedKycDocs, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("KYC submitted successfully.");
      checkKycStatus(vid);
    } catch (e) { toast.error(e.response?.data?.response || "KYC submission failed."); }
    finally { setIsKycLoading(false); }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) return toast.warning("The new password and confirm password do not match.");
    setIsPasswordLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.patch(`${StandardURL}/users/changepassword`, passwordForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Password updated.");
      setPasswordForm({ oldPassword: "", newPassword: "", confirmPassword: "" });
    } catch (e) { toast.error("Validation failed."); }
    finally { setIsPasswordLoading(false); }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${StandardURL}/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) {}
    localStorage.clear(); 
    setShowLogoutModal(false);
    navigate("/");
  };

  return (
    <DashboardLayout currentTab={currentTab} setCurrentTab={setCurrentTab} showLogoutModal={showLogoutModal} setShowLogoutModal={setShowLogoutModal} handleLogout={handleLogout}>
      {currentTab === "overview" && (
        <OverviewPage credentials={credentials} kycViewData={kycViewData} vid={vid} topupForm={topupForm} setTopupForm={setTopupForm} handleTopUpSubmit={handleTopUpSubmit} isTopupLoading={isTopupLoading} handleWebhookUpdate={handleWebhookUpdate} webhookUrl={webhookUrl} setWebhookUrl={setWebhookUrl} />
      )}
      {currentTab === "messaging" && (
        <MessagingPage manualPhone={manualPhone} setManualPhone={setManualPhone} addManualContact={addManualContact} fileInputRef={fileInputRef} handleFileUpload={handleFileUpload} isUploading={isUploading} uploadStats={uploadStats} removeContact={removeContact} messageData={messageData} setMessageData={setMessageData} handleSendMessage={handleSendMessage} isSending={isSending} historyData={historyData} scheduledData={scheduledData} handleRetryMessage={handleRetryMessage} handleDeleteScheduled={handleDeleteScheduled} filterParams={filterParams} setFilterParams={setFilterParams} handleFilterMessages={handleFilterMessages} handleUpdateScheduledTime={handleUpdateScheduledTime} />
      )}
      {currentTab === "contacts" && (
        <ContactsPage 
          contactsList={contactsList}
          isContactsLoading={isContactsLoading}
          categoryName={categoryName}
          setCategoryName={setCategoryName}
          manualContact={manualContact}
          setManualContact={setManualContact}
          stagedContacts={stagedContacts}
          handleStageManualContact={handleStageManualContact}
          handleRemoveStagedContact={handleRemoveStagedContact}
          handleSaveStagedContacts={handleSaveStagedContacts}
          handleDeleteRow={handleDeleteRow}
          handleClearJson={handleClearJson}
          handleUpdateCategory={handleUpdateCategory}
          handleUpdateContactJson={handleUpdateContactJson}
          fileInputRef={contactsFileInputRef}
          handleImportFile={handleImportContactsFile}
          isUploading={isUploading}
          
          batchMessageData={batchMessageData}
          setBatchMessageData={setBatchMessageData}
          isSendingBatch={isSendingBatch}
          handleSendBatchByCategory={handleSendBatchByCategory}
        />
      )}
      {currentTab === "security" && (
        <SecurityPage kycViewData={kycViewData} handleKycSubmit={handleKycSubmit} isKycLoading={isKycLoading} handleKycFileUpload={handleKycFileUpload} uploadedKycDocs={uploadedKycDocs} kycFileNames={kycFileNames} passwordForm={passwordForm} setPasswordForm={setPasswordForm} handlePasswordChange={handlePasswordChange} isPasswordLoading={isPasswordLoading} handleGenerateHashkey={handleGenerateHashkey} hashTimer={hashTimer} newHashkey={newHashkey} />
      )}
      {currentTab === "docs" && <DeveloperDocsPage vid={vid} />}
    </DashboardLayout>
  );
};

export default VidEnvironsnew;